// src/components/BillingIdentitySection.jsx
// Collecte de l'identité de facturation entreprise (B2B) — requis pour la facturation
// électronique obligatoire (réforme française + ViDA à l'échelle UE).
// L'IDENTIFIANT PIVOT DÉPEND DU PAYS :
//   - France       -> SIREN (9 chiffres) requis  [routage annuaire PPF]
//   - Autre UE     -> n° de TVA intracommunautaire requis  [e-reporting intra-UE, VIES]
//   - Hors UE      -> raison sociale (+ identifiant fiscal optionnel)  [e-reporting]
// Écrit sur `profiles` (siren, siret, vat_number, company_name, billing_*). Ces champs
// ne sont PAS protégés par le trigger anti-escalade -> modifiables par le client.
import React, { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";

// États membres de l'UE (ISO-3166-1 alpha-2) — pour décider FR / UE / hors-UE.
const EU = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE",
  "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE",
]);

// Préfixe TVA par pays (la Grèce utilise EL, pas GR).
const VAT_PREFIX = {
  AT: "AT", BE: "BE", BG: "BG", HR: "HR", CY: "CY", CZ: "CZ", DK: "DK", EE: "EE",
  FI: "FI", FR: "FR", DE: "DE", GR: "EL", HU: "HU", IE: "IE", IT: "IT", LV: "LV",
  LT: "LT", LU: "LU", MT: "MT", NL: "NL", PL: "PL", PT: "PT", RO: "RO", SK: "SK",
  SI: "SI", ES: "ES", SE: "SE",
};

// Liste pays proposée (UE prioritaire + quelques hors-UE fréquents).
const COUNTRIES = [
  { value: "FR", key: "france" },
  { value: "BE", key: "belgium" },
  { value: "LU", key: "luxembourg" },
  { value: "DE", key: "germany" },
  { value: "IT", key: "italy" },
  { value: "ES", key: "spain" },
  { value: "NL", key: "netherlands" },
  { value: "PT", key: "portugal" },
  { value: "AT", key: "austria" },
  { value: "IE", key: "ireland" },
  { value: "GR", key: "greece" },
  { value: "CH", key: "switzerlandNonEu" },
  { value: "GB", key: "unitedKingdomNonEu" },
  { value: "US", key: "unitedStatesNonEu" },
  { value: "OTHER", key: "other" },
];

// --- Validations ---
function luhnValid(num) {
  let sum = 0;
  const rev = num.split("").reverse();
  for (let i = 0; i < rev.length; i++) {
    let d = parseInt(rev[i], 10);
    if (i % 2 === 1) { d *= 2; if (d > 9) d -= 9; }
    sum += d;
  }
  return sum % 10 === 0;
}
const onlyDigits = (s) => (s || "").replace(/\D/g, "");
const cleanVat = (s) => (s || "").toUpperCase().replace(/[^0-9A-Z]/g, "");

const sirenState = (v, t) => {
  const d = onlyDigits(v);
  if (!d) return { ok: null, msg: "" };
  if (d.length !== 9) return { ok: false, msg: t("billingIdentity.validation.sirenLength") };
  if (!luhnValid(d)) return { ok: false, msg: t("billingIdentity.validation.sirenInvalid") };
  return { ok: true, msg: t("billingIdentity.validation.sirenValid") };
};
const siretState = (v, t) => {
  const d = onlyDigits(v);
  if (!d) return { ok: null, msg: "" };
  if (d.length !== 14) return { ok: false, msg: t("billingIdentity.validation.siretLength") };
  if (!luhnValid(d)) return { ok: false, msg: t("billingIdentity.validation.siretInvalid") };
  return { ok: true, msg: t("billingIdentity.validation.siretValid") };
};
// TVA UE : préfixe pays à 2 lettres + 2 à 12 caractères. On vérifie le préfixe si un
// pays UE est sélectionné (validation VIES = à faire côté serveur ultérieurement).
const vatState = (v, country, t) => {
  const s = cleanVat(v);
  if (!s) return { ok: null, msg: "" };
  if (!/^[A-Z]{2}[0-9A-Z]{2,12}$/.test(s)) {
    return { ok: false, msg: t("billingIdentity.validation.vatFormat") };
  }
  const expected = VAT_PREFIX[country];
  if (expected && !s.startsWith(expected)) {
    return { ok: false, msg: t("billingIdentity.validation.vatPrefix", { expected }) };
  }
  return { ok: true, msg: t("billingIdentity.validation.vatValid") };
};

export default function BillingIdentitySection({ profile }) {
  const { t } = useTranslation();
  const { user, fetchUserProfile } = useAuth();
  const [isBusiness, setIsBusiness] = useState(
    Boolean(profile?.siren || profile?.siret || profile?.vat_number || profile?.company_name)
  );
  const [f, setF] = useState({
    company_name: profile?.company_name || profile?.company || "",
    siren: profile?.siren || "",
    siret: profile?.siret || "",
    vat_number: profile?.vat_number || "",
    billing_address: profile?.billing_address || profile?.address || "",
    billing_postal_code: profile?.billing_postal_code || "",
    billing_city: profile?.billing_city || "",
    billing_country: profile?.billing_country || "FR",
    billing_email: profile?.billing_email || "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setSaved(false); };

  const country = f.billing_country;
  const isFR = country === "FR";
  const isEU = EU.has(country);

  const sv = useMemo(() => sirenState(f.siren, t), [f.siren, t]);
  const st = useMemo(() => siretState(f.siret, t), [f.siret, t]);
  const vv = useMemo(() => vatState(f.vat_number, country, t), [f.vat_number, country, t]);

  // Repropose le bon préfixe de TVA si l'utilisateur change de pays UE (hors FR).
  useEffect(() => {
    if (isBusiness && isEU && !isFR && !cleanVat(f.vat_number) && VAT_PREFIX[country]) {
      setF((p) => ({ ...p, vat_number: VAT_PREFIX[country] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  async function save() {
    setError("");
    if (isBusiness) {
      if (!f.company_name.trim()) { setError(t("billingIdentity.errors.companyRequired")); return; }
      if (isFR) {
        if (sv.ok !== true) { setError(t("billingIdentity.errors.sirenRequiredFrance")); return; }
        if (st.ok === false) { setError(st.msg); return; }
        if (vv.ok === false) { setError(vv.msg); return; }
      } else if (isEU) {
        if (vv.ok !== true) { setError(t("billingIdentity.errors.vatRequiredEu")); return; }
      } else {
        if (vv.ok === false) { setError(vv.msg); return; } // hors UE : identifiant optionnel
      }
    }
    setSaving(true);
    const payload = isBusiness
      ? {
          company_name: f.company_name.trim() || null,
          // SIREN/SIRET = France uniquement (sinon on nettoie pour éviter des données incohérentes)
          siren: isFR ? (onlyDigits(f.siren) || null) : null,
          siret: isFR ? (onlyDigits(f.siret) || null) : null,
          vat_number: cleanVat(f.vat_number) || null,
          billing_address: f.billing_address.trim() || null,
          billing_postal_code: f.billing_postal_code.trim() || null,
          billing_city: f.billing_city.trim() || null,
          billing_country: country || null,
          billing_email: f.billing_email.trim() || null,
        }
      : { siren: null, siret: null, vat_number: null }; // repasse en particulier
    const { error: e } = await supabase.from("profiles").update(payload).eq("id", user.id);
    setSaving(false);
    if (e) { setError(e.message); return; }
    setSaved(true);
    if (typeof fetchUserProfile === "function") fetchUserProfile(user.id).catch(() => {});
  }

  const flag = (s) => (s.ok === true ? "#2f7d3a" : s.ok === false ? "#b4443a" : "#8a8378");

  return (
    <div style={{ background: "#fff", border: "1px solid #e5e0d8", borderRadius: 14, padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 20, margin: 0 }}>{t("billingIdentity.title")}</h2>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer" }}>
          <input type="checkbox" checked={isBusiness} onChange={(e) => { setIsBusiness(e.target.checked); setSaved(false); }} />
          {t("billingIdentity.businessToggle")}
        </label>
      </div>
      <p style={{ color: "#6b6459", fontSize: 13, marginTop: 8, marginBottom: isBusiness ? 20 : 0, lineHeight: 1.5 }}>
        <Trans
          i18nKey="billingIdentity.description"
          components={{ siren: <strong />, vat: <strong /> }}
        />
      </p>

      {isBusiness && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label={t("billingIdentity.fields.country")}>
            <select className="bi-in" value={country} onChange={(e) => set("billing_country", e.target.value)}>
              {COUNTRIES.map((c) => (
                <option key={c.value} value={c.value}>{t(`billingIdentity.countries.${c.key}`)}</option>
              ))}
            </select>
          </Field>
          <Field label={t("billingIdentity.fields.companyName")}>
            <input className="bi-in" value={f.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder={t("billingIdentity.placeholders.companyName")} />
          </Field>

          {/* France : SIREN requis (+ SIRET/TVA optionnels) */}
          {isFR && (
            <>
              <Field label={t("billingIdentity.fields.siren")} hint={sv.msg} hintColor={flag(sv)}>
                <input className="bi-in" inputMode="numeric" value={f.siren} onChange={(e) => set("siren", e.target.value)} placeholder="123 456 789" />
              </Field>
              <Field label={t("billingIdentity.fields.siret")} hint={st.msg} hintColor={flag(st)}>
                <input className="bi-in" inputMode="numeric" value={f.siret} onChange={(e) => set("siret", e.target.value)} placeholder="123 456 789 00012" />
              </Field>
              <Field label={t("billingIdentity.fields.vatOptional")} hint={vv.msg} hintColor={flag(vv)} full>
                <input className="bi-in" value={f.vat_number} onChange={(e) => set("vat_number", e.target.value)} placeholder="FR 12 345678901" />
              </Field>
            </>
          )}

          {/* Autre UE : TVA requise */}
          {isEU && !isFR && (
            <Field label={t("billingIdentity.fields.vatRequired")} hint={vv.msg} hintColor={flag(vv)} full>
              <input className="bi-in" value={f.vat_number} onChange={(e) => set("vat_number", e.target.value)} placeholder={`${VAT_PREFIX[country] || "XX"}0123456789`} />
            </Field>
          )}

          {/* Hors UE : identifiant fiscal optionnel */}
          {!isEU && (
            <Field label={t("billingIdentity.fields.taxIdOptional")} hint={vv.msg} hintColor={flag(vv)} full>
              <input className="bi-in" value={f.vat_number} onChange={(e) => set("vat_number", e.target.value)} placeholder={t("billingIdentity.placeholders.taxId")} />
            </Field>
          )}

          <Field label={t("billingIdentity.fields.address")} full>
            <input className="bi-in" value={f.billing_address} onChange={(e) => set("billing_address", e.target.value)} placeholder={t("billingIdentity.placeholders.address")} />
          </Field>
          <Field label={t("billingIdentity.fields.postalCode")}>
            <input className="bi-in" value={f.billing_postal_code} onChange={(e) => set("billing_postal_code", e.target.value)} placeholder="75006" />
          </Field>
          <Field label={t("billingIdentity.fields.city")}>
            <input className="bi-in" value={f.billing_city} onChange={(e) => set("billing_city", e.target.value)} placeholder={t("billingIdentity.placeholders.city")} />
          </Field>
          <Field label={t("billingIdentity.fields.billingEmail")}>
            <input className="bi-in" type="email" value={f.billing_email} onChange={(e) => set("billing_email", e.target.value)} placeholder={t("billingIdentity.placeholders.billingEmail")} />
          </Field>
        </div>
      )}

      {error && <div style={{ marginTop: 14, color: "#b4443a", fontSize: 13 }}>{error}</div>}

      <div style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={save} disabled={saving}
          style={{ background: "#1a1a1a", color: "#f9f7f4", border: "none", borderRadius: 9, padding: "10px 22px", fontSize: 14, cursor: "pointer", opacity: saving ? 0.5 : 1 }}>
          {saving ? t("billingIdentity.saving") : t("billingIdentity.save")}
        </button>
        {saved && <span style={{ color: "#2f7d3a", fontSize: 13 }}>✓ {t("billingIdentity.saved")}</span>}
      </div>

      <style>{`
        .bi-in{width:100%;border:1px solid #d6d0c4;border-radius:8px;padding:9px 12px;font-size:14px;background:#fff;box-sizing:border-box}
        .bi-in:focus{outline:none;border-color:#c9a961}
      `}</style>
    </div>
  );
}

function Field({ label, hint, hintColor, full, children }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : "auto" }}>
      <label style={{ display: "block", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.04em", color: "#8a8378", marginBottom: 5 }}>{label}</label>
      {children}
      {hint ? <div style={{ fontSize: 12, color: hintColor || "#8a8378", marginTop: 4 }}>{hint}</div> : null}
    </div>
  );
}
